import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import keys from "lodash/keys";

type Item = { key: string; parents: string[] };
type CalculateExpandedState<T extends Item> = {
  expandedState: Record<string, boolean | undefined>;
  items: T[];
  isExpandable: (item: T) => boolean;
  forceExpandNewChildren?: boolean;
};

/**
 * Calculates the expanded state basing on the previous state and current items
 * - It expands new children by default
 * - It preserves the state of the children even if the children are not visible
 * - It resets the children state if the parent is collapsed
 */
export const calculateExpandedState = <T extends Item>({
  expandedState,
  items,
  isExpandable,
  forceExpandNewChildren = true
}: CalculateExpandedState<T>) => {
  const result = { ...expandedState };
  const children = items.filter((item) => !isEmpty(item.parents));

  // if the state of the child was not touched (is undefined) then expand the child
  for (const child of children) {
    if (isNil(expandedState[child.key]) && isExpandable(child) && forceExpandNewChildren) {
      result[child.key] = true;
    }
  }

  // if an item is not expandable, make sure its state is reset
  for (const item of items) {
    if (!isExpandable(item)) {
      keys(result).forEach((key) => {
        if (key.includes(item.key)) {
          delete result[key];
        }
      });
    }
  }

  // if an item is manually collapsed (false) then resets all its children state
  for (const item of items) {
    if (result[item.key] === false) {
      keys(result).forEach((key) => {
        if (key !== item.key && key.includes(`${item.key}_`)) {
          delete result[key];
        }
      });
    }
  }

  return result;
};
